// UX-Vorschlag P5: Inbox mit Kontext. Nur gerendert, wenn
// uxEnabled("ux_web_p5"); sonst zeigt routes/Inbox.tsx die bisherige Liste.
//
// - Titel mit Aufgabenbezug, Kommentar-Auszug unter dem Titel.
// - Eintraege zur selben Aufgabe stehen gruppiert unter EINER Kopfzeile.
// - Eigene Handlungen (Selbst-Zuweisung …) sind ausgeblendet, per Schalter
//   einblendbar. Das ist eine reine Anzeige-Entscheidung: am Server und an den
//   Benachrichtigungs-Einstellungen aendert sich nichts.
// - Aktionen stehen sichtbar in der Zeile: Antworten, Erledigt,
//   Zurückstellen, → Mein Tag.

import { useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { CheckCheck, Clock, MailOpen, MessageSquare, Sun } from "lucide-preact";
import { Button } from "../ui/Button";
import { SnoozePicker } from "./SnoozePicker";
import { SourceIcon } from "./SourceIcon";
import {
  contextTitle,
  countSelfPings,
  excerpt,
  groupInboxByTask,
  visibleInboxItems,
  type InboxItemCtx,
} from "../../lib/inboxContext";
import { relTime } from "../../lib/inboxTime";
import { replyToInboxItem } from "../../lib/uxApi";
import { addToMyDay } from "../../store/myDay";
import { toastSuccess } from "../../lib/toast";
import { InboxBulkBar, InboxRowCheckbox } from "./InboxBulkBar";
import type { InboxItem, InboxItemState } from "../../lib/api";
import { inboxKey, toggleOne, type InboxBulkAction } from "../../lib/inboxSelection";

export type InboxRowAction = "done" | "unread" | "snooze";

export interface InboxContextListProps {
  items: InboxItemCtx[];
  /** Label des aktiven Reiters, fuer die Liste und den Leerzustand. */
  tabLabel: string;
  onAction: (item: InboxItemCtx, action: InboxRowAction, snoozeUntil?: string) => Promise<void>;
  /** Leerzustand, wenn nach dem Ausblenden nichts uebrig ist. */
  empty: preact.ComponentChildren;
  /** #1399: Mehrfachauswahl. Ohne `onBulk` gibt es keine Kästchen. */
  tab?: InboxItemState;
  selected?: ReadonlySet<string>;
  onSelectionChange?: (next: Set<string>) => void;
  onBulk?: (items: InboxItem[], action: InboxBulkAction, snoozeUntil?: string) => Promise<void>;
}

const iconBtn =
  "inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors disabled:opacity-50";

export function InboxContextList({
  items,
  tabLabel,
  onAction,
  empty,
  tab,
  selected,
  onSelectionChange,
  onBulk,
}: InboxContextListProps): preact.JSX.Element {
  const [showSelf, setShowSelf] = useState(false);
  const selfCount = countSelfPings(items);
  const visible = visibleInboxItems(items, showSelf);
  const groups = groupInboxByTask(visible);
  // „Alle auswählen“ meint die SICHTBAREN Einträge; ausgeblendete eigene
  // Aktionen bleiben unberührt.
  const selectable = tab !== undefined && selected !== undefined && onSelectionChange && onBulk;

  return (
    <div>
      {selfCount > 0 && (
        <label class="mb-3 flex items-center gap-2 text-xs text-[color:var(--color-on-surface-variant)]">
          <input
            type="checkbox"
            checked={showSelf}
            onChange={(e) => setShowSelf((e.target as HTMLInputElement).checked)}
          />
          Eigene Aktionen anzeigen ({selfCount})
        </label>
      )}
      {groups.length === 0 ? (
        empty
      ) : (
        <>
        {selectable && (
          <InboxBulkBar
            visible={visible}
            tab={tab}
            selected={selected}
            onSelectionChange={onSelectionChange}
            onBulk={onBulk}
          />
        )}
        <ul
          class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]"
          aria-label={`Inbox — ${tabLabel}`}
        >
          {groups.map((g) => (
            <li key={g.key}>
              {(g.items.length > 1 || g.items[0]?.task_title) && (
                <div class="flex items-baseline gap-2 px-4 pt-3 text-xs">
                  {g.link ? (
                    <Link
                      href={g.link}
                      class="min-w-0 truncate font-semibold text-[color:var(--color-on-surface)] hover:text-[color:var(--color-primary)]"
                    >
                      {g.label}
                    </Link>
                  ) : (
                    <span class="min-w-0 truncate font-semibold text-[color:var(--color-on-surface)]">{g.label}</span>
                  )}
                  {g.project_name && (
                    <span class="shrink-0 text-[color:var(--color-on-surface-variant)]">{g.project_name}</span>
                  )}
                  {g.items.length > 1 && (
                    <span class="ml-auto shrink-0 tabular-nums text-[color:var(--color-on-surface-variant)]">
                      {g.items.length} Einträge
                    </span>
                  )}
                </div>
              )}
              <ul>
                {g.items.map((item) => (
                  <ContextRow
                    key={inboxKey(item)}
                    item={item}
                    onAction={onAction}
                    select={
                      selectable
                        ? {
                            checked: selected.has(inboxKey(item)),
                            onToggle: () => onSelectionChange(toggleOne(selected, inboxKey(item))),
                          }
                        : undefined
                    }
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
        </>
      )}
    </div>
  );
}

function ContextRow({
  item,
  onAction,
  select,
}: {
  item: InboxItemCtx;
  onAction: InboxContextListProps["onAction"];
  select?: { checked: boolean; onToggle: () => void };
}): preact.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");

  const isUnread = item.state === "unread";
  const doneByTask = item.task_done === true;
  const quote = excerpt(item.body);
  // Server sagt, ob eine Antwort moeglich ist; aeltere Server: nur mit Projektaufgabe.
  const canReply = (item.can_reply ?? item.task_id !== null) && item.state !== "done";
  const canMyDay = item.task_id !== null || item.personal_task_id != null;

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch {
      /* Mutationen toastet api(); onAction rollt selbst zurueck */
    } finally {
      setBusy(false);
    }
  };

  const sendReply = (): Promise<void> =>
    run(async () => {
      const text = reply.trim();
      if (!text) return;
      // Kommentar + Eintrag erledigt in EINEM Aufruf (Server).
      await replyToInboxItem(item.source_type, item.source_id, text);
      setReply("");
      setReplyOpen(false);
      // Lokalen Zustand + Glocke/Zaehler nachziehen (PATCH ist idempotent).
      await onAction(item, "done");
    });

  const toMyDay = (): Promise<void> =>
    run(async () => {
      if (item.task_id !== null) await addToMyDay({ task_id: item.task_id });
      else if (item.personal_task_id != null) await addToMyDay({ personal_task_id: item.personal_task_id });
      toastSuccess("Zu „Mein Tag“ hinzugefügt");
    });

  return (
    <li class={isUnread ? "bg-[color:var(--color-primary-container)]/10" : ""}>
      <div class="flex items-start gap-3 px-4 py-3">
        {select && <InboxRowCheckbox checked={select.checked} title={contextTitle(item)} onToggle={select.onToggle} />}
        <span class="mt-0.5 text-[color:var(--color-on-surface-variant)]">
          <SourceIcon type={item.kind ?? item.source_type} />
        </span>
        <div class="min-w-0 flex-1">
          {item.link ? (
            <Link
              href={item.link}
              class="block text-sm font-medium leading-snug text-[color:var(--color-on-surface)] hover:text-[color:var(--color-primary)] focus:outline-none focus:underline"
            >
              {contextTitle(item)}
            </Link>
          ) : (
            <span class="block text-sm font-medium leading-snug text-[color:var(--color-on-surface)]">
              {contextTitle(item)}
            </span>
          )}
          {quote && (
            <span class="mt-0.5 block line-clamp-2 text-xs text-[color:var(--color-on-surface-variant)]">„{quote}"</span>
          )}
          <span class="mt-0.5 block text-micro text-[color:var(--color-on-surface-variant)]">
            {relTime(item.created_at)}
            {item.state === "snoozed" && item.snooze_until && (
              <span class="ml-2 text-[color:var(--color-warning,#A16207)]">
                · zurückgestellt bis {new Date(item.snooze_until).toLocaleDateString("de-DE")}
              </span>
            )}
            {doneByTask && <span class="ml-2">· Aufgabe erledigt</span>}
          </span>

          <div class="mt-1.5 -ml-1.5 flex flex-wrap items-center gap-1" aria-label="Aktionen">
            {canReply && (
              <button type="button" class={iconBtn} disabled={busy} onClick={() => setReplyOpen(!replyOpen)}>
                <MessageSquare size={13} aria-hidden="true" />
                Antworten
              </button>
            )}
            {item.state !== "done" && (
              <button
                type="button"
                class={iconBtn}
                disabled={busy}
                onClick={() => void run(() => onAction(item, "done"))}
                aria-label="Als erledigt markieren"
              >
                <CheckCheck size={13} aria-hidden="true" />
                Erledigt
              </button>
            )}
            {item.state === "done" && !doneByTask && (
              <button
                type="button"
                class={iconBtn}
                disabled={busy}
                onClick={() => void run(() => onAction(item, "unread"))}
                aria-label="Als ungelesen markieren"
              >
                <MailOpen size={13} aria-hidden="true" />
                Ungelesen
              </button>
            )}
            {item.state !== "snoozed" && item.state !== "done" && (
              <div class="relative">
                <button
                  type="button"
                  class={iconBtn}
                  disabled={busy}
                  onClick={() => setShowSnooze(!showSnooze)}
                  aria-label="Zurückstellen"
                >
                  <Clock size={13} aria-hidden="true" />
                  Zurückstellen
                </button>
                {showSnooze && (
                  <SnoozePicker
                    onPick={(until) => {
                      setShowSnooze(false);
                      void run(() => onAction(item, "snooze", until));
                    }}
                    onClose={() => setShowSnooze(false)}
                  />
                )}
              </div>
            )}
            {canMyDay && item.state !== "done" && (
              <button type="button" class={iconBtn} disabled={busy} onClick={() => void toMyDay()}>
                <Sun size={13} aria-hidden="true" />
                → Mein Tag
              </button>
            )}
          </div>

          {replyOpen && (
            <form
              class="mt-2 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void sendReply();
              }}
            >
              <input
                type="text"
                value={reply}
                onInput={(e) => setReply((e.target as HTMLInputElement).value)}
                placeholder="Antwort als Kommentar …"
                aria-label="Antwort"
                class="min-w-0 flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] sm:text-sm"
                autoFocus
              />
              <Button type="submit" variant="primary" size="xs" loading={busy} disabled={reply.trim() === ""}>
                Senden
              </Button>
            </form>
          )}
        </div>
        {isUnread && (
          <span class="mt-2 size-1.5 shrink-0 rounded-full bg-[color:var(--color-primary)]" aria-label="ungelesen" />
        )}
      </div>
    </li>
  );
}
